import {Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import {DamageCase, DamageArea, DamageType} from '../../../../models/models';
import {Translation} from '../../../../models/translation';
import {DamageAdministrationService} from '../../../../services/damage-administration.service';
import {LoginService} from '../../../../services/login.service';
import {CheckService} from '../../../../services/check.service';
import {ExternalStatePipe} from '../../../../pipes/externalState.pipe';
import {InitialsPipe} from '../../../../pipes/initials.pipe';
import {SafeHtmlPipe} from '../../../../pipes/safeHtml.pipe';
import {GridComponent} from '@syncfusion/ej2-angular-grids';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-damage-case-closed',
  templateUrl: './damage-case-closed.component.html',
  styleUrls: ['./damage-case-closed.component.css'],
  providers: [[ExternalStatePipe], [InitialsPipe], [SafeHtmlPipe]]
})
export class DamageCaseClosed implements OnInit {
  @ViewChild('damageCaseGrid', {static: true}) private damageCaseGrid: GridComponent;
  @ViewChild('feedStateTemplate', { static: true }) feedStateTemplate!: TemplateRef<any>;
  @ViewChild('externalStateTemplate', { static: true }) externalStateTemplate!: TemplateRef<any>;

  public translation: Translation;
  public language: string;
  public locale: string;
  public selectedIndex = 0;
  public damageCaseDataSource: DamageCase[] = [];
  public damageTypes: DamageType[] = [];
  public damageAreas: DamageArea[] = [];
  public damageCase = new DamageCase();

  public toast = Swal.mixin({toast: true, position: 'top-right', timer: 2000, timerProgressBar: false, showConfirmButton: false});

  constructor(private loginService: LoginService, private checkService: CheckService, private damageAdministrationService: DamageAdministrationService) {}

  ngOnInit() {
    window.scrollTo(0, 0);
    this.loginService.checkToken();
    this.checkService.checkRight(5);
    this.translation = JSON.parse(localStorage.getItem('Translation'));
    this.language = localStorage.getItem('Language');
    this.getDamageAreas();
    this.getDamageTypes();
    this.loadDataForGrid();
    this.damageCase.DamageDocuments = [];
    this.damageCase.Damages = [];
  }
  
  public loadDataForGrid() {
    this.damageAdministrationService.getClosedDamageCasesByPrimaryAddressId(+sessionStorage.getItem('PrimaryAddressId')).subscribe(response => {
      if (response.Ok) {
        this.damageCaseDataSource = response.Obj;
        this.initGrid();
      } else if (response.Msg === 'Internal Server Error') {
        console.log(response.Ex);
      }
      if (response.Msg) {
        this.toast.fire({title: response.Msg, icon: response.Ok ? 'success' : 'error'});
      }
    }), error => {
      this.toast.fire(this.translation.InternalServerError, '', 'error');
    };
  }

  public getDamageAreas() {
    this.damageAdministrationService.getDamageAreas().subscribe(response => {
      if (response.Ok) {
        this.damageAreas = response.Obj;
      } else if (response.Msg === 'Internal Server Error') { console.log(response.Ex); }
      if (response.Msg) { this.toast.fire({title: response.Msg, icon: response.Ok ? 'success' : 'error'}); }
    }, error => { this.toast.fire(this.translation.InternalServerError, '', 'error'); });
  }
  
  public getDamageTypes() {
    this.damageAdministrationService.getDamageTypes().subscribe(response => {
      if (response.Ok) {
        this.damageTypes = response.Obj;
      } else if (response.Msg === 'Internal Server Error') { console.log(response.Ex); }
      if (response.Msg) { this.toast.fire({title: response.Msg, icon: response.Ok ? 'success' : 'error'}); }
    }, error => { this.toast.fire(this.translation.InternalServerError, '', 'error'); });
  }

  public getDamageAreaDescription(id: number): string {
    const area = this.damageAreas.find(a => a.Id === id);
    if (!area) return '';
    return this.getDescriptionByLanguage(area);
  }
    
  public getDamageTypeDescription(id: number): string {
    const type = this.damageTypes.find(t => t.Id === id);
    if (!type) return '';
    return this.getDescriptionByLanguage(type);
  }
  
  private getDescriptionByLanguage(item: { Code: number; DescriptionDE: string; DescriptionIT: string; DescriptionEN: string; DescriptionFR: string }): string {
    switch (this.language) {
      case 'DE': return item.Code + ' - ' + item.DescriptionDE;
      case 'IT': return item.Code + ' - ' + item.DescriptionIT;
      case 'EN': return item.Code + ' - ' + item.DescriptionEN;
      case 'FR': return item.Code + ' - ' + item.DescriptionFR;
      default: return item.Code + ' - ' + item.DescriptionDE;
    }
  }
    
  public initGrid() {
    this.damageCaseGrid.allowExcelExport = true;
    this.damageCaseGrid.allowPaging = true;
    this.damageCaseGrid.pageSettings = {pageCount: 5, pageSize: 20};
    this.damageCaseGrid.toolbar = ['Search'];
    this.damageCaseGrid.allowResizing = true;
    this.damageCaseGrid.editSettings = {allowEditing: false, allowAdding: false, allowDeleting: false, mode: 'Normal'};
  }

  public onDataBound() {
     this.damageCaseGrid.autoFitColumns();
  }
    
  goBack(): void {
    this.selectedIndex = 0;
  }
    
  damageCaseRowSelect(args: any) {
    this.damageCase = args.data;
    this.damageAdministrationService.getDamageCaseById(this.damageCase.Id).subscribe(response => {
      if (response.Ok) {
        this.damageCase = response.Obj;

        // Sort Feeds by TimeStamp
        this.damageCase.Feeds = response.Obj.Feeds.sort((a, b) => {
          return new Date(b.TimeStamp).getTime() - new Date(a.TimeStamp).getTime();
        });

        // Filter out all documents, keep CostEstimate and Invoice
        this.damageCase.DamageDocuments = this.damageCase.DamageDocuments.filter(o => o.DocumentType === '0' || o.DocumentType === "2"); 
        this.selectedIndex = 1;

        // Reset FeedState to 0, after User clicked on row with new message.
        if (response.Obj.FeedState === 1) {
          this.damageAdministrationService.resetFeedStateByCaseId(this.damageCase.Id).subscribe(response => {
            if (response.Ok) {
              this.damageCase.FeedState = 0;
              
              // Replace old case within array with the new case (updated feed state)
              const index = this.damageCaseDataSource.findIndex(item => item.Id === this.damageCase.Id);
              if (index !== -1) {
                this.damageCaseDataSource[index] = this.damageCase;
                this.damageCaseDataSource = [...this.damageCaseDataSource];
              }

            } else if (response.Msg === 'Internal Server Error') { console.log(response.Ex); }
            if (response.Msg) { this.toast.fire({title: response.Msg, icon: response.Ok ? 'success' : 'error'}); }
          }, error => { this.toast.fire(this.translation.InternalServerError, '', 'error'); });
        }

      } else if (response.Msg === 'Internal Server Error') { console.log(response.Ex); }
      if (response.Msg) { this.toast.fire({title: response.Msg, icon: response.Ok ? 'success' : 'error'}); }
    }, error => { this.toast.fire(this.translation.InternalServerError, '', 'error'); });
  }

  onPreviewClick(dataUrl: string, filename: string) {
    const mimeType = dataUrl.match(/^data:(.*);base64,/)?.[1] ?? '';

    let body = '';

    if (mimeType.startsWith('image/')) {
      body = `<img src="${dataUrl}" style="max-width:100%;height:auto;" />`;
    } else if (mimeType === 'application/pdf') {
      body = `<embed src="${dataUrl}" type="application/pdf" width="100%" height="100%" />`;
    } else {
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${filename}</title>
          <style>
            body {
              margin: 0;
              background: #111;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
            }
          </style>
        </head>
        <body>
          ${body}
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    window.open(url, '_blank');
  }

  IsImage(base64: string): boolean {
    return base64?.trim().startsWith('data:image');
  }
}
